import { Params, redirect } from 'react-router-dom';
import { useApplicationStore } from '@data';
import { ensureFeatureFlagsFetched } from '@lib/firebase';
import { SERVICES_ROUTE_PATHNAME } from '@router/routes/routePathnames';
import { serviceIsAddictionTreatment } from '@pages/SchedulePage/utils';
import { getVisitTypeUrl } from '@router/utils';
import { postIntakeQuizSessionsIdResponses } from 'api/generated/intake-quiz/intake-quiz';
import { loadServiceTypesByState } from './utils';

export type IntakeQuizLoaderResponse = {
  visitTypeId: string;
};

export default async function intakeQuizLoader({
  params,
}: {
  params: Params<string>;
}): Promise<IntakeQuizLoaderResponse | Response> {
  await ensureFeatureFlagsFetched();

  const { visitTypeId } = params;
  const { auth, flags, serviceTypes, stateCode, setVisitTypeId } =
    useApplicationStore.getState();

  const redirectToServices = redirect(
    `${SERVICES_ROUTE_PATHNAME}${stateCode ? `?state=${stateCode}` : ''}`,
  );

  if (!visitTypeId) return redirectToServices;

  const intakeQuiz = useApplicationStore.getState().intakeQuiz;

  // Users with a completed or in-progress session bypass flag and service-type
  // validation — they already qualified when they first entered. `state` and
  // `serviceTypes` are not persisted, so they'd always fail the service-type
  // check after a hard refresh.
  if (intakeQuiz.completed) {
    return redirect(getVisitTypeUrl(visitTypeId));
  }

  const hasActiveSession = Boolean(intakeQuiz.sessionId && intakeQuiz.currentQuestion);

  if (!hasActiveSession) {
    if (
      auth?.accessToken ||
      !flags.experimentMatQuestionsEnabled ||
      !flags.experimentMatQuestions
    ) {
      return redirectToServices;
    }

    let loadedServiceTypes = serviceTypes;
    if (!loadedServiceTypes.length && stateCode) {
      loadedServiceTypes = (await loadServiceTypesByState(stateCode)) ?? [];
    }

    const parentServiceType = loadedServiceTypes.find((serviceType) =>
      serviceType.visitTypes.some((v) => v.visitTypeId === visitTypeId),
    );

    if (
      !parentServiceType ||
      !serviceIsAddictionTreatment(parentServiceType.serviceTypeId)
    ) {
      return redirectToServices;
    }
  }

  setVisitTypeId(visitTypeId);

  // Probe a persisted mid-flow session before React mounts — avoids any
  // flash of stale-session content by validating the session now.
  if (intakeQuiz.sessionId && intakeQuiz.currentQuestion) {
    if (intakeQuiz.history.length > 0) {
      const last = intakeQuiz.history[intakeQuiz.history.length - 1]!;
      try {
        await postIntakeQuizSessionsIdResponses(intakeQuiz.sessionId, {
          QuestionKey: last.question.Key,
          SelectedCodes: [...last.selectedCodes],
        });
      } catch (error) {
        type AppError = { status?: number; details?: { errorCode?: string } };
        const appError = error as AppError | null;
        const isNetworkError = typeof appError?.status !== 'number';
        if (!isNetworkError) {
          const errorCode = appError?.details?.errorCode;
          if (errorCode === 'SessionAlreadyCompleted') {
            return redirect(getVisitTypeUrl(visitTypeId));
          }
          // Any other server error — session is stale, reset for a fresh start.
          intakeQuiz.reset();
        }
        // Network error — proceed optimistically; page handles it on submit.
      }
    } else {
      // Persisted session with no history (user was on Q1) — force fresh start.
      intakeQuiz.reset();
    }
  }

  // Experiment enrollment is deferred to the page itself, after the BE
  // returns the first question — users who can't bootstrap a session never
  // see the experiment and shouldn't be counted in the cohort.
  return { visitTypeId };
}
